import { useCallback, useEffect, useMemo, useState } from 'react';
import { Crop, CropType } from '../../../../../constants/CropConstants';
import { cropData, getSeedRate } from '../../../DanishSeedingModelV1SeedRate';
import {
  CalculatePrescriptionLimitParams,
  SeedingCalculatorStrategy,
} from './SeedingCalculatorStrategy/SeedingCalculatorStrategy';

const getSelectionKey = (crop: Crop, date: Date | null): string | null => {
  if (!date || Number.isNaN(date.getTime())) return null;
  return `${crop}:${date.getTime()}`;
};

interface UseSeedingPrescriptionLimitCalculatorResult {
  calculationResult: number | null;
  shouldShowOutsideRangeWarning: boolean;
  requestSeedingDateChange: (date: Date | null) => void;
  requestCropChange: (crop: Crop) => void;
  confirmOutsideRangeDate: () => void;
  cancelOutsideRangeDate: () => void;
}

interface PendingSelection {
  changedInput: 'crop' | 'date';
  crop: Crop;
  date: Date;
}

const isDateOutsideCropRange = (crop: Crop, date: Date): boolean => {
  return Boolean(cropData[crop] && getSeedRate(crop, date).isOutsideRange);
};

export const useSeedingPrescriptionLimitCalculator = (
  params: CalculatePrescriptionLimitParams,
  setSeedingDate: (date: Date | null) => void,
  setSelectedCrop: (crop: Crop) => void,
  seedingCalculatorStrategy: SeedingCalculatorStrategy
): UseSeedingPrescriptionLimitCalculatorResult => {
  const [calculationResult, setCalculationResult] = useState<number | null>(null);
  const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null);
  const [acceptedSelectionKey, setAcceptedSelectionKey] = useState<string | null>(() =>
    getSelectionKey(params.selectedCrop, params.seedingDate)
  );

  const currentSelectionKey = useMemo(
    () => getSelectionKey(params.selectedCrop, params.seedingDate),
    [params.selectedCrop, params.seedingDate]
  );

  useEffect(() => {
    if (!params.selectedCrop || params.selectedCrop === CropType.NONE || !params.seedingDate) {
      setCalculationResult(null);
      return;
    }

    const isAcceptedSelection =
      currentSelectionKey !== null && currentSelectionKey === acceptedSelectionKey;

    const options = isAcceptedSelection ? { allowDateOutsideRange: true } : {};
    const result = seedingCalculatorStrategy.calculatePrescriptionLimit(params, options);

    setCalculationResult(result.ok ? result.value : null);
  }, [params, currentSelectionKey, acceptedSelectionKey]);

  const requestSeedingDateChange = useCallback(
    (date: Date | null) => {
      if (date && isDateOutsideCropRange(params.selectedCrop, date)) {
        setPendingSelection({ changedInput: 'date', crop: params.selectedCrop, date });
        return;
      }

      setAcceptedSelectionKey(null);
      setPendingSelection(null);
      setSeedingDate(date);
    },
    [params.selectedCrop, setSeedingDate]
  );

  const requestCropChange = useCallback(
    (crop: Crop) => {
      if (params.seedingDate && isDateOutsideCropRange(crop, params.seedingDate)) {
        setPendingSelection({ changedInput: 'crop', crop, date: params.seedingDate });
        return;
      }

      setAcceptedSelectionKey(null);
      setPendingSelection(null);
      setSelectedCrop(crop);
    },
    [params.seedingDate, setSelectedCrop]
  );

  const confirmOutsideRangeDate = useCallback(() => {
    if (!pendingSelection) return;

    setAcceptedSelectionKey(getSelectionKey(pendingSelection.crop, pendingSelection.date));
    if (pendingSelection.changedInput === 'crop') {
      setSelectedCrop(pendingSelection.crop);
    } else {
      setSeedingDate(pendingSelection.date);
    }
    setPendingSelection(null);
  }, [pendingSelection, setSeedingDate, setSelectedCrop]);

  const cancelOutsideRangeDate = useCallback(() => {
    setPendingSelection(null);
  }, []);

  return {
    calculationResult,
    shouldShowOutsideRangeWarning: pendingSelection !== null,
    requestSeedingDateChange,
    requestCropChange,
    confirmOutsideRangeDate,
    cancelOutsideRangeDate,
  };
};
